import { createAppRequest } from '@/services/request';
import type { CrmScopedViewMode } from '@/modules/zy-crm/utils/crm-view';
import type { ApiRequestOptions } from './types/common';
import type {
  CreateQuoteTeamMemberDto,
  QuoteDrawerProfile,
  QuoteDrawerSummaryPanel,
  QuoteItem,
  QuoteWorkflowDetailResult,
} from './types/quote';

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm/quotes';

type DetailScopeOptions = ApiRequestOptions & {
  params?: Record<string, unknown>;
  viewMode?: CrmScopedViewMode;
};

const unwrapQuoteDetail = (resp: unknown): QuoteItem => {
  if (resp && typeof resp === 'object') {
    const obj = resp as Record<string, unknown>;
    const candidates = [
      obj,
      obj.data,
      obj.result,
      obj.payload,
      (obj.response as Record<string, unknown> | undefined)?.data,
    ];
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') continue;
      const record = candidate as Record<string, unknown>;
      if (typeof record.uuid === 'string') {
        return candidate as QuoteItem;
      }
    }
  }
  throw new Error('报价单详情返回结构异常');
};

export async function queryQuoteDetail(uuid: string, options?: DetailScopeOptions) {
  const { viewMode, params, ...rest } = options || {};
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: 'GET',
    params: {
      ...(params || {}),
      ...(viewMode ? { viewMode } : {}),
    },
    ...rest,
  });
  return unwrapQuoteDetail(resp);
}

export async function queryQuoteDrawerProfile(
  uuid: string,
  options?: DetailScopeOptions,
) {
  const { viewMode, params, ...rest } = options || {};
  return request<QuoteDrawerProfile>(`${BASE_PATH}/${uuid}/drawer-profile`, {
    method: 'GET',
    params: {
      ...(params || {}),
      ...(viewMode ? { viewMode } : {}),
    },
    ...rest,
  });
}

export async function createQuoteTeamMember(
  uuid: string,
  payload: CreateQuoteTeamMemberDto,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteQuoteTeamMember(
  uuid: string,
  teamMemberUuid: string,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/${uuid}/team-members/${teamMemberUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function submitQuoteApproval(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<QuoteItem>(`${BASE_PATH}/${uuid}/submit-approval`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function withdrawQuoteApproval(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<QuoteItem>(`${BASE_PATH}/${uuid}/withdraw-approval`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function getQuoteWorkflowDetail(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<QuoteWorkflowDetailResult>(
    `${BASE_PATH}/${uuid}/workflow-detail`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}
